import { useCallback } from "react"
import { fboApi, type SupplierRoleProperty } from "@/api/fbo-api.client"
import type { GetToken } from "@/api/ats-sync-api.client"
import type { SearchableSelectOption } from "@/components/ats-fbo/fields/searchable-select"
import type { OrganizationDto } from "@/api/fbo-api.types"
import { useODataSearch, useMockSearch } from "./use-odata-search"
import { env } from "@/lib/env"
import {
  internalCompanies as mockInternalCompanies,
  clientOrganizations as mockClientOrgs,
  workerOrganizations as mockWorkerOrgs,
} from "@/components/ats-fbo/mock-data"

type OrgRole = "InternalRole" | "ClientRole"

const orgToOption = (o: OrganizationDto): SearchableSelectOption => ({
  id: String(o.Id),
  name: `${o.DisplayName} (${o.Code})`,
  canonicalName: o.DisplayName,
})

const mockToOption = (o: { id: number | string; name: string }): SearchableSelectOption => ({
  id: String(o.id),
  name: o.name,
  canonicalName: o.name,
})

export function useOrganizationSearch(role: OrgRole, selectedId: string | null, getToken: GetToken) {
  const useMock = env.useMockData || !env.fboApiUrl
  const numericId = selectedId ? Number(selectedId) : null

  const searchFn = role === "InternalRole" ? fboApi.searchInternalOrgs : fboApi.searchClientOrgs
  const fetchResults = useCallback(
    (term: string) => searchFn(term, getToken, numericId),
    [searchFn, getToken, numericId]
  )

  const mockData = role === "InternalRole" ? mockInternalCompanies : mockClientOrgs

  const mockResult = useMockSearch(mockData, mockToOption, selectedId)
  const odataResult = useODataSearch(fetchResults, orgToOption)

  return useMock ? mockResult : odataResult
}

const PROFILE_TO_SUPPLIER_ROLE: Record<string, SupplierRoleProperty> = {
  WorkerCanadianInc: "IsOrganizationIndependentContractorRole",
  WorkerUnitedStatesLLC: "IsOrganizationLimitedLiabilityCompanyRole",
  WorkerSubVendor: "IsOrganizationSubVendorRole",
}

export function useWorkerOrgSearch(profileTypeCode: string | null, selectedId: string | null, getToken: GetToken) {
  const useMock = env.useMockData || !env.fboApiUrl
  const roleProperty = profileTypeCode ? PROFILE_TO_SUPPLIER_ROLE[profileTypeCode] : undefined
  const numericId = selectedId ? Number(selectedId) : null

  const fetchResults = useCallback(
    (term: string) => {
      if (!roleProperty) return Promise.resolve([])
      return fboApi.searchSupplierOrgs(term, roleProperty, getToken, numericId)
    },
    [roleProperty, getToken, numericId]
  )

  const mockResult = useMockSearch(mockWorkerOrgs, mockToOption, selectedId)
  const odataResult = useODataSearch(fetchResults, orgToOption)

  return useMock ? mockResult : odataResult
}
